import {
	type BaseMarkerDrawableState,
	type DrawableLink,
	type DrawableState,
	DrawableTag,
	type LabelDrawableState,
	type LineDrawableState,
	type MarkerDrawableState,
	MarkerType,
	type MaskDrawableState,
	type VerticalSeparationDrawableState,
} from '@ikegps/core-api';
import { CollectionModelDrawableVerticalSeparation } from '../drawable/vertical.separation.drawable';
import { CollectionModel } from '../collection.data';
import { CollectionModelDrawable } from '../drawable/drawable';
import { CollectionModelDrawableLabel } from '../drawable/label.drawable';
import { CollectionModelDrawableLine } from '../drawable/line.drawable';
import { CollectionModelDrawableMarker } from '../drawable/marker.drawable';
import { CollectionModelDrawableMarkerOffset } from '../drawable/marker.offset.drawable';
import { CollectionModelDrawableMask } from '../drawable/mask.drawable';

function writeLink(collection: CollectionModel, fieldId: string): DrawableLink | null {
	const collectionField = collection.getFieldDeep(fieldId);
	if (collectionField == null) {
		return null;
	}

	const formField = collectionField.schema;
	return {
		name: formField.id,
		formID: collectionField.parent.instanceId,
		text: collectionField.annotationLabel,
	};
}

function writeLabelDrawable(label: CollectionModelDrawableLabel): LabelDrawableState {
	return {
		id: label.id,
		tag: DrawableTag.LABEL,
		x: label.x,
		y: label.y,
		text: label.text.length === 1 ? label.text[0] : undefined,
	};
}

function writeMarkerDrawable(
	collection: CollectionModel,
	drawable: CollectionModelDrawableMarker
): MarkerDrawableState {
	const links = drawable.label.links.map((field) => writeLink(collection, field.id)).filter((link) => !!link);
	if (drawable.type === MarkerType.BASE) {
		throw new Error(
			`Base Marker Drawable was passed to writeMarkerDrawable but should be passed to writeBaseMarkerDrawable instead`
		);
	}
	return {
		id: drawable.id,
		type: drawable.type,
		x: drawable.x,
		y: drawable.y,
		tag: DrawableTag.MARKER,
		label: writeLabelDrawable(drawable.label),
		link: links.length > 0 ? links : undefined,
		pair: drawable.pair ? drawable.pair : undefined,
		insight: drawable.insight ? drawable.insight : undefined,
	};
}

function writeBaseMarkerDrawable(
	collection: CollectionModel,
	drawable: CollectionModelDrawableMarker
): BaseMarkerDrawableState {
	const links = drawable.label.links.map((field) => writeLink(collection, field.id)).filter((link) => !!link);
	if (drawable.type !== MarkerType.BASE) {
		throw new Error(
			`Non-Base Marker Drawable was passed to writeBaseMarkerDrawable but should be passed to writeMarkerDrawable instead`
		);
	}
	return {
		id: drawable.id,
		type: drawable.type,
		x: drawable.x,
		y: drawable.y,
		tag: DrawableTag.MARKER,
		label: writeLabelDrawable(drawable.label),
		link: links.length > 0 ? links : undefined,
		pair: drawable.pair ? drawable.pair : undefined,
		insight: drawable.insight ? drawable.insight : undefined,
		offset: drawable.offset ?? -1,
	};
}

function writeLineDrawable(collection: CollectionModel, drawable: CollectionModelDrawableLine): LineDrawableState {
	const startPoint = drawable.start;
	const endPoint = drawable.end;

	return {
		id: drawable.id,
		tag: DrawableTag.LINE,
		start: { x: startPoint.x, y: startPoint.y },
		end: { x: endPoint.x, y: endPoint.y },
		label: writeLabelDrawable(drawable.label),
	};
}

function writeMaskDrawable(drawable: CollectionModelDrawableMask): MaskDrawableState {
	return {
		id: drawable.id,
		tag: DrawableTag.MASK,
		pukeko_data: drawable.pukekoLocation,
	};
}

function writeVerticalSeparationDrawable(
	drawable: CollectionModelDrawableVerticalSeparation
): VerticalSeparationDrawableState {
	return {
		id: drawable.id,
		tag: DrawableTag.VERTICAL_SEPARATION,
		label: writeLabelDrawable(drawable.label),
		topMarkerId: drawable.topMarkerId,
		bottomMarkerId: drawable.bottomMarkerId,
		fieldId: drawable.fieldId,
	};
}

export class DrawableWriter {
	static write(collection: CollectionModel, drawable: CollectionModelDrawable): DrawableState | null {
		if (CollectionModelDrawableMarkerOffset.isBaseOffset(drawable)) {
			return writeMarkerDrawable(collection, drawable as any);
		}
		if (CollectionModelDrawableMarker.isMarker(drawable)) {
			if (CollectionModelDrawableMarker.isBase(drawable)) {
				return writeBaseMarkerDrawable(collection, drawable);
			}
			return writeMarkerDrawable(collection, drawable);
		}
		if (CollectionModelDrawableLine.isLine(drawable)) {
			return writeLineDrawable(collection, drawable);
		}
		if (CollectionModelDrawableMask.isMask(drawable)) {
			return writeMaskDrawable(drawable);
		}
		if (drawable instanceof CollectionModelDrawableVerticalSeparation) {
			return writeVerticalSeparationDrawable(drawable);
		}
		return null;
	}
}
